import React from "react";
import { Modal, Form, Input, Row, Col, Card, Button, Select, Avatar } from "antd";
import { PersonnelService } from "../../services/PersonnelService";
import { DepartementService } from "../../services/DepartementService";
import Toast from "../../helpers/Toast";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { catchError } from "../../services/DaoService";
import CustomFormItem from "../utils/form/CustomFormItem";
import style from "./PersonnelCreateForm.module.scss";
import PhoneInput from "react-phone-number-input";
import { MailOutlined } from "@ant-design/icons";

const PersonnelCreateForm = ({ visible, onCreate, onCancel, departement, personnel }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [creating, setCreating] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [personnels, setPersonnels] = React.useState([]);
  const [departements, setDepartements] = React.useState([]);

  React.useEffect(() => {
    if(departement) {
      form.setFieldsValue({ departement_id: departement.id });
    }
    if(personnel) {
      form.setFieldsValue({ personnel_id: personnel.id });
    }
  }, [departement, personnel, form]);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      PersonnelService.getInstance().all(),
      DepartementService.getInstance().all()
    ]).then(([personnelData, departementData]) => {
      setPersonnels(personnelData.data);
      setDepartements(departementData.data);
    }).catch((error) => {
      catchError(error);
    }).finally(() => {
      setLoading(false);
    });
  }, [visible]);

  const handleSave = (isRedirection) => {
    setCreating(true);
    form
      .validateFields()
      .then((values) => {
        PersonnelService.getInstance()
          .create(values)
          .then((response) => {
            Toast.success(response.message);
            form.resetFields();
            onCreate(response.data);
            if (isRedirection) {
              navigate(`/personnel/${response.data.uid}`);
            }
          })
          .catch((error) => {
            const errors = error.response.data;
            if (errors.validation && Object.keys(errors?.errors).length) {
              setValidationErrors(errors.errors);
            } else {
              catchError(error);
            }
          })
          .finally(() => {
            setCreating(false);
          });
      })
      .catch(() => {
        setCreating(false);
      });
  };

  return (
    <Modal destroyOnHidden
      open={visible}
      onCancel={onCancel}
      title="Créer un nouveau personnel"
      footer={[
        <>
          <Row gutter={24}>
            <Col>
              <Button
                key="cancel"
                onClick={onCancel}
                className={style.ButtonNew}
              >
                Annuler
              </Button>
            </Col>
            <Col>
              <Button
                key="save-show"
                type="primary"
                loading={creating}
                onClick={() => handleSave(true)}
                className={style.ButtonNew}
              >
                Enregistrer et Afficher
              </Button>
            </Col>
            <Col>
              <Button
                key="save-again"
                type="primary"
                loading={creating}
                onClick={() => handleSave(false)}
                className={style.ButtonNew}
              >
                Enregistrer et Créer
              </Button>
            </Col>
          </Row>
        </>
      ]}
    >
      <Card style={{ marginBottom: 5 }}>
        <Form
          layout="vertical"
          form={form}
          name="createPersonnelForm"
          initialValues={{
            enabled: 1,
          }}
        >
          <Row gutter={24}>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Prénoms"
                name="prenoms"
                rules={[{ required: true, message: "Veuillez saisir le prénoms!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="text" />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Nom"
                name="nom"
                rules={[{ required: true, message: "Veuillez saisir le nom!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="text" />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Email"
                name="email"
                rules={[{ required: true, message: "Veuillez saisir le email!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Input type="email" suffix={<MailOutlined />} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Téléphone"
                name="telephone"
                rules={[{ required: true, message: "Veuillez saisir le telephone!" }]}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <PhoneInput defaultCountry="SN" placeholder="Saisir le numéro de téléphone" />
              </CustomFormItem>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Supérieur hiérarchique"
                name="personnel_id"
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Select placeholder="Sélectionner un supérieur hiérarchique"
                  allowClear loading={loading}
                  showSearch size="large"
                  optionFilterProp="name" options={personnels.map((personnel) => ({
                    label: <>
                      <Avatar src={personnel.avatar_link} /> {personnel.name}
                    </>,
                    value: personnel.id,
                    name: `${personnel.name}`
                  }))} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem
                label="Departement de rattachement"
                name="departement_id"
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              >
                <Select placeholder="Sélectionner un département de rattachement"
                  allowClear loading={loading}
                  showSearch
                  optionFilterProp="label" options={departements.map((departement) => ({
                    label: `${departement.nom} (${departement.code})`,
                    value: departement.id,
                  }))} />
              </CustomFormItem>
            </Col>
            <Col xs={24} md={24} lg={12}>
              <CustomFormItem label="Type Agent" name="permanent" rules={[{ required: true, message: "Veuillez saisir le statut!" }]} validationErrors={validationErrors} setValidationErrors={setValidationErrors}>
                <Select placeholder="Sélectionner le statut" allowClear loading={loading} showSearch size="large" optionFilterProp="name" options={[{ label: "Permanent", value: 1 }, { label: "Temporaire", value: 0 }]} />
              </CustomFormItem>
            </Col>
          </Row>

        </Form>
      </Card>
    </Modal>
  );
};

PersonnelCreateForm.propTypes = {
  visible: PropTypes.bool,
  onCreate: PropTypes.func,
  onCancel: PropTypes.func,
  departement: PropTypes.object,
  personnel: PropTypes.object,
};

export default PersonnelCreateForm;

